import React from 'react';
import { faIR, toDoranDate } from '@doranjs/core';
import { colors } from '../theme/palette/colors';
import moment from 'moment-jalaali';
import 'moment/locale/fa';
import { immediateToast } from 'utils/toast';
import { Banks } from 'utils/banks';
import {
    gatewayPermissionList,
    permissionListSelector,
    setGatewayPermissionList,
    useAppSelector,
} from 'app/store';
import { Gateway } from 'features/gateways/types/gateway.types';
import { useGateway } from 'hooks';

export const toSelectOptions = (_options: { [key: string]: string }) => {
    const options = Object.keys(_options).map((key) => {
        return {
            label: _options[key],
            value: key,
        };
    });

    return options;
};

export function findBank(bankName: string) {
    const pureName = bankName.replace(/بانک/g, '').trim();
    return Banks.filter((item) => item.name === pureName)[0];
}

export function findBankLogo(bankName: string): string {
    const bank = findBank(bankName);
    if (bank) return '/images/bank-logo/' + bank.icon + '.svg';
    return '';
}

export function findBankLogoColor(bankName: string): string {
    const bank = findBank(bankName);
    if (bank) return bank.color;
    return '';
}

export function moneyFormat(number: number | string): string {
    number = moneyFormatInt(number);
    let money = new Intl.NumberFormat('en-US', {
        style: 'decimal',
    }).format(number);

    if (money === 'NaN') return '0';

    return money;
}

export function moneyFormatInt(value: string | number): number {
    value = value + '';
    return parseInt(value.replace(/\D/g, ''));
}

export function toNumber(string: string) {
    string = string + '';
    return string.replace(/[^\d]/g, '');
}

export function toJalali(date: string, format: string = 'YYYY/MM/DD') {
    const parsed = toDoranDate(date, {
        timeZone: 'Asia/Tehran',
        locale: faIR,
    });

    return parsed?.format(format) ?? '';
}

export function toJalaliHuman(date: string, withMinute: boolean = false) {
    return toJalali(date, withMinute ? 'DD MMMM YYYY HH:mm' : 'DD MMMM YYYY');
}

export function toGregorian(jalaliDate: string) {
    const parsed = toDoranDate(perToEnDigits(jalaliDate), {
        timeZone: 'Asia/Tehran',
        locale: faIR,
    });

    return parsed?.formatGregorian('YYYY-MM-DD') ?? '';
}

export function toGregorianWithTime(jalaliDateTime: string): string {
    const normalized = perToEnDigits(jalaliDateTime)
        .replace(/\s+ساعت\s+/, ' ')
        .trim();
    const m = moment(normalized, 'jYYYY/jMM/jDD HH:mm');

    return m.toDate().toISOString();
}

export function toJalaliDateTime(isoUtc: string): string {
    moment.loadPersian({ dialect: 'persian-modern', usePersianDigits: true });

    return moment.utc(isoUtc).local().format('jYYYY/jMM/jDD HH:mm');
}

// time convertor
export const timeConverter = (
    timestamp: string,
    type?: 'dayMonth' | 'fullDay' | 'date' | 'time' | 'dayName',
): string => {
    if (!timestamp) return '';

    moment.loadPersian({ dialect: 'persian-modern' });

    const dayMonth = moment(timestamp).locale('fa').format('jD jMMMM'); // "15 آبان"
    const fullDay = moment(timestamp)
        .locale('fa')
        .format('dddd jD jMMMM jYYYY'); // "دوشنبه 15 آبان 1402"
    const date = moment(timestamp).locale('fa').format('jD jMMMM jYYYY'); // "15 آبان 1402"
    const time = moment(timestamp).locale('fa').format('[ساعت] HH:mm'); // "ساعت 16:00"
    const dayName = moment(timestamp).locale('fa').format('dddd'); // "دوشنبه"
    const fullDateTime = moment(timestamp)
        .locale('fa')
        .format('jD jMMMM jYYYY HH:mm'); // "15 آبان 1402 ساعت 16:00"

    switch (type) {
        case 'dayMonth':
            return dayMonth;
        case 'fullDay':
            return fullDay;
        case 'date':
            return date;
        case 'time':
            return time;
        case 'dayName':
            return dayName;
        default:
            return fullDateTime;
    }
};

// eng to per digit converter
export const EngToPerConverter = (num: any) => {
    const persianNumbers =
        '\u06F0\u06F1\u06F2\u06F3\u06F4\u06F5\u06F6\u06F7\u06F8\u06F9';
    return new String(num).replace(/[0123456789]/g, (d) => {
        return persianNumbers[Number(d)];
    });
};

// handle no row text for data tables
export function noRowsTitle(label: string, searchObj?: any) {
    return `هیچ ${label} ${
        searchObj && Object.keys(searchObj)?.length > 0
            ? 'پیدا نشد'
            : 'وجود ندارد'
    }.`;
}

// number convertor
export const engToPerDigits = (input: string): string => {
    if (!input) return '';

    const englishDigits: string[] = [
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
    ];
    const persianDigits: string[] = [
        '۰',
        '۱',
        '۲',
        '۳',
        '۴',
        '۵',
        '۶',
        '۷',
        '۸',
        '۹',
    ];

    let output = '';

    for (let i = 0; i < input?.length; i++) {
        const char = input[i];
        const index = englishDigits?.indexOf(char);
        output += index !== -1 ? persianDigits[index] : char;
    }

    return output;
};

export const perToEnDigits = (input: string): string => {
    if (!input) return '';

    const englishDigits: string[] = [
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
    ];
    const persianDigits: string[] = [
        '۰',
        '۱',
        '۲',
        '۳',
        '۴',
        '۵',
        '۶',
        '۷',
        '۸',
        '۹',
    ];

    let output = '';

    for (let i = 0; i < input.length; i++) {
        const char = input[i];
        const index = persianDigits.indexOf(char);
        output += index !== -1 ? englishDigits[index] : char;
    }

    return output;
};

export const idGenerator = (): number => {
    // create new id
    const timestamp: number = new Date().getTime();
    const shortenedTimestamp: number = timestamp % 100000;
    const randomNumber: number = Math.round(Math.random() * 1000000);
    const newID: number = shortenedTimestamp + randomNumber;

    return newID;
};

export const nullToStringObjectValue = (obj: any) => {
    Object.keys(obj).map(
        (key) => (obj[key] = obj[key] === null ? '' : obj[key]),
    );

    return obj;
};

export function stringToColor(string: string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
}

export const getServerErrorMessage = (
    error: any,
    fallback = 'خطای غیرمنتظره رخ داده است',
): string => {
    const response = error?.data?.response ?? error?.data ?? error;
    const errorBag = response?.errors ?? response?.data;

    if (errorBag && typeof errorBag === 'object') {
        const firstValue = Object.values(errorBag).find((value) => {
            if (Array.isArray(value)) return Boolean(value[0]);
            return Boolean(value);
        });

        if (Array.isArray(firstValue) && firstValue[0]) {
            return String(firstValue[0]);
        }

        if (firstValue) {
            return String(firstValue);
        }
    }

    if (response?.message) {
        return String(response.message);
    }

    return fallback;
};

export const handleServerErrors = (
    error: any,
    setErrors?: (errors: any) => void,
) => {
    // RTK Query exposes Laravel errors directly on error.data. Older
    // endpoints in this project may still wrap them inside response, so
    // support both shapes.
    const response = error?.data?.response ?? error?.data ?? error;
    const errorBag = response?.errors ?? response?.data;
    const message = response?.message;

    if (errorBag && typeof errorBag === 'object') {
        const normalizedErrors = Object.fromEntries(
            Object.entries(errorBag).map(([key, value]) => [
                key,
                Array.isArray(value) ? value[0] : value,
            ]),
        );

        if (setErrors) {
            setErrors(normalizedErrors);
            return;
        }

        Object.values(normalizedErrors).forEach((value) => {
            if (value) {
                immediateToast('error', { message: String(value) });
            }
        });
        return;
    }

    if (message) {
        immediateToast('error', { message: String(message) });
        return;
    }

    immediateToast('error', { message: 'خطای غیرمنتظره رخ داده است' });
};

export const statusColors: Record<
    string,
    { background: string; color: string }
> = {
    success: {
        background: colors.alpha.status.success,
        color: colors.status.success.main,
    },
    warning: {
        background: colors.alpha.status.warning,
        color: colors.status.warning.main,
    },
    error: {
        background: colors.alpha.status.error,
        color: colors.status.error.main,
    },
    search: {
        background: colors.alpha.brandDark[16],
        color: colors.brand[400],
    },
};

export const usePermissionChecker = () => {
    const userPermissions = useAppSelector(permissionListSelector);
    const gatewayPermissions = useAppSelector(gatewayPermissionList);
    const gateway = useGateway();

    const hasPermission = (
        permissionKey: string,
        customGateway?: Gateway,
    ): boolean => {
        const permissionSection = permissionKey.split('-')[0];
        const targetGateway = customGateway ?? gateway;

        if (permissionSection === 'user') {
            return userPermissions?.includes(permissionKey) || false;
        }

        if (permissionSection === 'gateway') {
            const gatewayPermission = gatewayPermissions?.find(
                (item) => item.gateway_id === targetGateway?.id,
            );
            return (
                gatewayPermission?.permissions?.includes(permissionKey) || false
            );
        }

        return false;
    };

    return { hasPermission };
};

export const useGatewayPermission = (
    gatewayList: any,
    profile: any,
    dispatch: any,
) => {
    if (gatewayList && profile) {
        const result = gatewayList?.map((gateway: any) => {
            const matchingPermission = gateway?.permissions?.find(
                (item: any) => item?.user_id === profile?.id,
            );

            return {
                gateway_id: gateway?.id,
                permissions: matchingPermission?.permissions || [],
            };
        });

        dispatch(setGatewayPermissionList(result));
    } else {
        dispatch(setGatewayPermissionList([]));
    }
};

export const copyTextToClipboard = async (
    value: string | null | undefined,
): Promise<boolean> => {
    const text = value ?? '';

    try {
        if (navigator.clipboard) {
            await navigator.clipboard.writeText(text);
        } else {
            // Fallback method for non-secure contexts
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document?.execCommand('copy');
            document.body.removeChild(textArea);
        }

        immediateToast('success', {
            message: 'با موفقیت کپی شد',
            timeout: 2000,
        });

        return true;
    } catch (err: any) {
        immediateToast('error', {
            message: err?.message ?? 'در زمان کپی، خطایی رخ داده است',
            timeout: 2000,
        });

        return false;
    }
};

